/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ApisApi, ApiType, Config, CoreV1Api, Exec, HttpError, KubeConfig, KubernetesListObject,
  KubernetesObject, KubernetesObjectApi, PortForward, V1APIResourceList, V1CustomResourceDefinition, V1DaemonSet, V1Deployment, V1Job, V1JobSpec, V1Pod, V1StatefulSet, V1Volume, V1VolumeMount
} from "@kubernetes/client-node"
import * as crypto from "crypto"
import * as yaml from 'js-yaml'
import * as _ from "lodash"
import * as net from 'net'
import * as os from "os"
import * as path from "path"
import { promisify } from "util"
import {
  ApiConstructor, CheckableWorkloadType, ConfigMap, ConfQueryParam, ExecParam, FetchLogParam, ForwardOrExecResult, ForwardParam, GetConfReturn, IMetadata,
  JobParam, K8SResourceMeta, K8SResourceType, KindGroupVersion, KubernetesOperator, KV, KVString, PodQueryParam, Secret, SecretQueryParam, SimplePodSpec
} from "yam-types"
import {
  DEFAULT_KUBE_CONFIG,
  DEFAULT_STATUS_CHECK_TIME, EXEC_DEFAULT_CMD, K8S_REQUEST_TIMEOUT,
  LOCK_PREFIX,
  MAX_BYTES_PER_LINE, MAX_FORWARD_CHECK_TIMES, MAX_JOB_STATUS_CHECK_TIME, MAX_LINES_OF_LOGS, STATUS_CHECK_INTERVAL
} from "../constants"
import { EnvPatternValues } from "../main"
import { EngineConfig } from "../settings.d"
import { KubernetesEnvironment } from "../types.d"
import { createLogger, fileExists, fsExist, fsRead, fsWrite, reduceWildcardEnvValue, wait } from "../util"
import request = require('request')

type KubeApiClient = {
  kubeConf: KubeConfig
  coreV1Api: CoreV1Api,
  k8sApi: KubernetesObjectApi
}

type Cluster = string

type APICache = { [key: Cluster]: { cacheTime: number, kgv: KindGroupVersion[] } }

const DRY_RUN_RESULT = {
  body: { kind: "Status", metadata: {}, inDryRunMode: true },
  response: { statusCode: 202, body: { kind: "Status", metadata: {}, inDryRunMode: true }, statusMessage: "Accepted - Dry Run" } as request.Response
}

// Query 500 Kubernetes Objects at Most
const LIMIT_QUERY_NUM = 500

const DRY_RUN_DUMMY_CONFIG = yaml.dump({
  apiVersion: "v1",
  kind: 'Config',
  'current-context': "dummy",
  clusters: [{ cluster: { server: "https://127.0.0.1:6443" }, name: "dummy" }],
  contexts: [{ name: "dummy", context: { cluster: "dummy", user: "dummy" } }],
  users: [{ name: "dummy", user: { token: "dummy" } }]
})

export const ENGINE_BASE_PATH = path.join(os.homedir(), ".yam")
export const CACHE_API_PATH = path.join(ENGINE_BASE_PATH, 'cached-api.yaml')

const LEVEL_DISABLE = 'silent'

const API_CACHE_TTL = 180000

let tmpLevel = 'info'
/**
 * Provide common methods to control multiple Kubernetes cluster
 */
export default class KubernetesOperatorClient implements KubernetesOperator {

  public appliedK8SResources = new Map<string, KubernetesObject[]>()
  public deletedK8SResources = new Map<string, KubernetesObject[]>()

  private static apiResourceVersionMap: APICache = {}

  private appMetadata: IMetadata
  private builtInLabels: KVString
  private builtInAnnotations: KVString
  private clusterConfigs: Map<Cluster, KubeApiClient> = new Map()
  private currentCluster: KubernetesEnvironment
  private dryRunMode = false
  private templateOutput?: boolean
  private dynamicParams?: Map<string, KV>
  private engineSettings?: EngineConfig
  private clusterNamespaces: Map<Cluster, Set<string>> = new Map()

  private log = createLogger('kubernetes')

  private constructor(
    clusters: KubernetesEnvironment[], appMetadata: IMetadata, dryRun: boolean,
    templateOutput?: boolean, settings?: EngineConfig, params?: Map<string, KV>
  ) {
    this.engineSettings = settings
    this.appMetadata = appMetadata
    this.dryRunMode = dryRun
    this.dynamicParams = params
    this.templateOutput = templateOutput
    this.builtInLabels = {
      app: appMetadata.app,
      "managed-by": settings?.clientConf.engineName || "yam-engine"
    }
    this.builtInAnnotations = {}
    const metaLabelMapping = settings?.clientConf.appendMetaLabels
    const metaAnnotationMapping = settings?.clientConf.appendMetaAnnotations
    if (metaLabelMapping) {
      for (const meta in metaLabelMapping) {
        if (metaLabelMapping[meta]) {
          this.builtInLabels[metaLabelMapping[meta]] = appMetadata[meta] as string || ''
        }
      }
    }
    if (metaAnnotationMapping) {
      for (const meta in metaAnnotationMapping) {
        if (metaAnnotationMapping[meta]) {
          this.builtInAnnotations[metaAnnotationMapping[meta]] = appMetadata[meta] as string || ''
        }
      }
    }
    clusters.forEach(cluster => {
      const kubeConf = new KubeConfig()
      if (!dryRun) {
        const kubeConfLoc = cluster.kubeConfPath || DEFAULT_KUBE_CONFIG()
        if (this.log.isLevelEnabled('trace')) {
          this.log.trace(`fetched kubeConfig source: ${JSON.stringify(kubeConfLoc)}`)
        }
        if (typeof kubeConfLoc === "object") {
          this.log.info(`kubeConfig is provided with object: ${cluster.name} - ${cluster.kubeConfContextName}`)
          kubeConf.loadFromString(yaml.dump(kubeConfLoc))
          kubeConf.setCurrentContext(cluster.kubeConfContextName)
          const currentCluster = kubeConf.getCurrentCluster()
          // Special logic for object type kube config, kubernetes SDK will not handle TLS in expected way, won't enter this in most cases
          if (currentCluster) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            currentCluster.skipTLSVerify = true
          }
        } else if (kubeConfLoc.indexOf('kind: Config') !== -1) {
          this.log.info(`kubeConfig is provided with raw string data: ${cluster.name} - ${cluster.kubeConfContextName}`)
          kubeConf.loadFromString(kubeConfLoc)
          kubeConf.setCurrentContext(cluster.kubeConfContextName)
        } else if (fileExists(kubeConfLoc)) {
          this.log.debug(`kubernetes client config loaded from: ${kubeConfLoc}`)
          kubeConf.loadFromFile(kubeConfLoc)
          kubeConf.setCurrentContext(cluster.kubeConfContextName)
        } else if (fileExists(Config.SERVICEACCOUNT_TOKEN_PATH)) {
          kubeConf.loadFromCluster()
          this.log.debug(`kubernetes client config loaded, in-cluster mode.`)
        } else {
          throw new Error(`kube config not found`)
        }
      } else {
        kubeConf.loadFromString(DRY_RUN_DUMMY_CONFIG)
        this.log.info(`skip load kube-config and switch cluster context in dry-run mode`)
      }
      const coreV1Api = kubeConf.makeApiClient(CoreV1Api)
      const k8sApi = KubernetesObjectApi.makeApiClient(kubeConf)
      this.log.debug(`kubernetes API client initialized: ${cluster.name}`)
      this.clusterConfigs.set(cluster.name, {
        kubeConf,
        k8sApi,
        coreV1Api
      })
    })
  }

  static async create(
    k8sClusters: KubernetesEnvironment[], appMetadata: IMetadata, dryRun: boolean, useCachedDiscovery?: boolean,
    templateOutput?: boolean, customizedMapping?: EnvPatternValues<KVString>[], settings?: EngineConfig, params?: Map<string, KV>
  ): Promise<KubernetesOperatorClient> {
    if (k8sClusters.length === 0) {
      throw new Error('no environment provided')
    }
    const kClient = new KubernetesOperatorClient(k8sClusters, appMetadata, dryRun, templateOutput, settings, params)
    if (dryRun) {
      kClient.log.info(`skip fetch version and groupKind info in dry-run mode, 'apiVersion' might be 'unknown'.`)
      return kClient
    }

    // By default, discovery cache is 3 minutes, but for command mode, API discovery results could be reused
    if (await fsExist(CACHE_API_PATH)) {
      const data = await fsRead(CACHE_API_PATH)
      if (data.length > 0) {
        const cached = yaml.load(data.toString()) as APICache
        if (useCachedDiscovery && cached[k8sClusters[0].name]) {
          kClient.log.debug(`use cached apiVersion mapping, skip calling discovery API`)
          this.apiResourceVersionMap = cached
          k8sClusters.forEach(cluster => this.mergeCustomizedApiVersionMapping(kClient, cluster, customizedMapping))
          return kClient
        }
        // check cache time, if not too early for all target clusters, use cached api definition
        if (k8sClusters.every(c => cached[c.name] && (Date.now() - cached[c.name].cacheTime <= API_CACHE_TTL))) {
          kClient.log.debug(`use cached apiVersion mapping, cache is not expired`)
          this.apiResourceVersionMap = cached
          return kClient
        }
      }
    }
    kClient.log.info(`kubernetes client created, start API discovery.`)
    for (const cluster of k8sClusters) {
      await kClient.pullNamespaces(cluster.name)
      kClient.log.info(`cluster ${cluster.name} connected`)

      // discover APIs and Resources by checking /apis & apis/groupVersion
      const apisApi = kClient.makeApiClient(ApisApi, cluster.name)
      const apiGroups = await apisApi.getAPIVersions()
      const kindGroupVersions = [] as KindGroupVersion[]
      const allGroups = apiGroups.body.groups.map(g => `/apis/${g.preferredVersion?.groupVersion}`)
      allGroups.push('/api/v1')
      await Promise.all(allGroups.map(discoverPath => (async () => {
        try {
          const { body, response } = await kClient.rawRequest(discoverPath, 'get', undefined, cluster.name, undefined)
          kClient.log.debug(`discovery for ${discoverPath} finished, status: ${response.statusCode}`)
          if (kClient.log.isLevelEnabled('trace')) {
            kClient.log.trace(`content of ${discoverPath} is: ${JSON.stringify(body)}`)
          }
          const apiResources = yaml.load(body as string) as V1APIResourceList
          if (apiResources.resources) {
            const tmp = apiResources.groupVersion.split('/')
            apiResources.resources.forEach(res => {
              // skip sub resources like: deployment/scale, deployment/status etc.
              if (res.name.indexOf('/') === -1) {
                kindGroupVersions.push({
                  kind: res.kind,
                  name: res.name,
                  apiGroup: tmp[0],
                  version: tmp[1],
                  namespacedRes: res.namespaced
                })
              }
            })
          } else {
            kClient.log.warn(`discovery for ${discoverPath} failed, no resource definition found.`)
          }
        } catch (err) {
          kClient.log.error(err)
        }
      })()))
      this.apiResourceVersionMap[cluster.name] = { cacheTime: Date.now(), kgv: kindGroupVersions }
      const str = kindGroupVersions.map(r => `\t${r.kind}\t${r.apiGroup}/${r.version || ''}`).join('\n')
      if (kClient.log.isLevelEnabled('trace')) {
        kClient.log.trace(`${kindGroupVersions.length} resources are defined on ${cluster.name} cluster: \n${str}\n=================`)
      }
      this.mergeCustomizedApiVersionMapping(kClient, cluster, customizedMapping)
    }
    await fsWrite(CACHE_API_PATH, yaml.dump(this.apiResourceVersionMap))
    return kClient
  }

  setCurrentContext(current: KubernetesEnvironment): void {
    if (!this.clusterConfigs.has(current.name)) {
      throw new Error('kube-context not found: ' + current.name)
    }
    this.currentCluster = current
  }

  async find<T>(resource: K8SResourceMeta, labelSelector?: KVString | string, fieldSelector?: string, cluster?: string): Promise<T[]> {
    const ns = resource.namespace || this.appMetadata.namespace
    const apiVer = resource.apiVersion || this.getApiVersionsByKind(resource.kind, undefined, cluster)
    this.log.info(`start fetching ${apiVer} ${resource.kind}: matching ${resource.name || fieldSelector || JSON.stringify(labelSelector)} in namespace '${ns}'`)
    if (this.dryRunMode) {
      return []
    }
    try {
      // find specific name
      if (resource.name) {
        this.log.info(`start fetching ${resource.kind}: ${resource.name} in namespace '${ns}'`)
        const { body, response } = await this.getApiClient(cluster).k8sApi.read({
          metadata: {
            name: resource.name,
            namespace: ns
          },
          apiVersion: apiVer,
          kind: resource.kind
        })
        this.log.info(`${resource.kind} '${resource.name}' fetched`)
        if (this.log.isLevelEnabled("trace")) {
          this.log.trace(`status: ${response.statusCode}, raw results: \n${yaml.dump(body)}`)
        }
        return [body as T]
      }
      // find by label selector or all resources in namespace
      this.log.info(`start fetching ${resource.kind}: ${fieldSelector || JSON.stringify(labelSelector)} in namespace '${ns}'`)
      const resourceDef = this.getResourceDefinition(resource.kind, undefined, cluster)
      if (!resourceDef) {
        throw new Error(`resource definition of '${resource.kind}' isn't found in cluster`)
      }
      // compose label selector
      const tmp = []
      let labelSelectorQuery = '&labelSelector='
      if (labelSelector) {
        if (typeof labelSelector === "string") {
          // String, eg. key=value,key2=value2
          labelSelectorQuery += encodeURIComponent(labelSelector)
        } else {
          // KVString
          for (const key in labelSelector) {
            tmp.push(key + '=' + labelSelector[key])
          }
          labelSelectorQuery += encodeURIComponent(tmp.join(','))
        }
      }
      // compose url path of GET API
      const apiPrefix = `${apiVer?.indexOf('/') === -1 ? 'api' : 'apis'}/${apiVer}`
      const nsPrefix = `${resourceDef.namespacedRes ? `/namespaces/${ns}` : ''}/${resourceDef.name}`
      const path = `${apiPrefix}${nsPrefix}?limit=${LIMIT_QUERY_NUM}${labelSelectorQuery}&labelSelector=${encodeURIComponent(fieldSelector || '')}`
      // send raw request to API server
      const { body, response } = await this.rawRequest(path, 'get', undefined, cluster)
      const results = JSON.parse(body as string) as KubernetesListObject<T>

      this.log.info(`${results.items?.length || 0} items of ${resource.kind} fetched, status: ${response.statusCode}`)
      if (this.log.isLevelEnabled("trace")) {
        this.log.trace(`raw results: \n${yaml.dump(body)}`)
      }
      return results.items.map(item => (
        {
          apiVersion: results.apiVersion,
          kind: resource.kind,
          ...item,
        }
      ))
    } catch (ex) {
      if (ex instanceof HttpError) {
        // not found, then use create method instead
        if (ex.statusCode === 404) {
          this.log.warn(`no ${resource.kind} found with the query criterial`)
          return []
        }
      }
      throw ex
    }
  }

  async apply(resource: string | KubernetesObject | KubernetesObject[], replaceMode?: boolean, cluster?: string): Promise<void> {
    if (typeof resource === "string") {
      try {
        resource = yaml.load(resource) as KV
      } catch (e) {
        resource = yaml.loadAll(resource as string) as KubernetesObject[]
      }
    }
    // in case of string/object array
    if (resource instanceof Array) {
      for (const res of resource) {
        if (!res) {
          continue
        }
        if (Object.keys(res).every(x => x.match(/^\d+$/))) {
          // FIX: in case of array-array
          for (const index in res) {
            await this.apply((res as KV)[index] as KubernetesObject, replaceMode, cluster)
          }
        } else {
          await this.apply(res, replaceMode, cluster)
        }
      }
      return
    }

    const resObj = resource as KubernetesObject
    if (!resObj.metadata || !resObj.kind || !resObj.metadata.name) {
      throw new Error('applying resource is not valid: \n' + yaml.dump(resObj))
    }
    const defaultLabels = {} as KVString
    if (this.engineSettings?.clientConf?.appendParamLabels) {
      const dynamicLabels = this.engineSettings.clientConf.appendParamLabels
      const values = this.dynamicParams?.get(cluster || this.currentCluster.name)
      if (values) {
        for (const key in dynamicLabels) {
          defaultLabels[dynamicLabels[key]] = ((values[key] || '') as string).toString()
        }
      }
    }

    resObj.metadata.labels = Object.assign(
      {},
      resObj.kind === "Namespace" ? {} : this.builtInLabels,
      defaultLabels,
      resObj.metadata?.labels
    )

    resObj.metadata.annotations = Object.assign({}, resObj.metadata?.annotations, this.builtInAnnotations)
    if (resObj.kind !== "Namespace" && !resObj.metadata.namespace) {
      resObj.metadata.namespace = this.appMetadata.namespace
    }

    const resName = resObj.metadata.name
    if (!resObj.apiVersion) {
      resObj.apiVersion = this.getApiVersionsByKind(resObj.kind)
      this.log.debug(`add missing apiVersion for ${resObj.kind} - ${resName}: ${resObj.apiVersion}`)
    }
    const api = this.getApiClient(cluster).k8sApi
    try {
      if (resObj.kind === K8SResourceType.ConfigMap && resName.startsWith(LOCK_PREFIX)) {
        this.log.info(`locking Kubernetes, lock entry is: '${resName}' in namespace ${resObj.metadata.namespace}`)
      } else {
        this.log.info(`${replaceMode ? 'replace' : 'create/patch'} kubernetes resource: ${resObj.kind} - ${resName}...`)
        if (resObj.kind !== K8SResourceType.Secret) {
          this.log.info("---------------------------------------------\n" + yaml.dump(resObj, { noRefs: true }))
        } else {
          this.log.info("---------------------------------------------")
          this.log.info(`*** kubernetes secrets will NOT be printed ***`)
        }
        this.log.info("---------------------------------------------")
      }
      if (!this.dryRunMode) {
        await api.read(resObj)
        const { body } = await (replaceMode ? api.replace(resObj) : api.patch(resObj, undefined, undefined, undefined, undefined, {
          headers: {
            // FIX: defaults to application/strategic-merge-patch+json in higher version of Kubernetes SDK, it might be not compatible with some CRDs
            "content-type": "application/merge-patch+json"
          }
        }))
        this.log.info(`${resObj.kind}- ${resName} patched`)
        if (this.log.isLevelEnabled("trace")) {
          this.log.trace(`raw results: \n${yaml.dump(body)}`)
        }
      }
      if (!this.appliedK8SResources.has(resObj.kind)) {
        this.appliedK8SResources.set(resObj.kind, [])
      }
      this.appliedK8SResources.get(resObj.kind)?.push(resObj)

      if (resObj.kind === K8SResourceType.Namespace) {
        // add namespace to clusterNamespaces
        this.clusterNamespaces
          .get(cluster || this.currentCluster.name)
          ?.add(resObj.metadata.name)
      }

      // template mode
      if (this.templateOutput) {
        console.log('---\n' + yaml.dump(resObj))
      }
    } catch (e) {
      if (e instanceof HttpError) {
        // not found, then use create method instead
        if (e.statusCode === 404) {
          const { body } = await api.create(resObj)
          this.log.info(`${resObj.kind} - ${resObj.apiVersion} - ${resName} created`)

          // merge CRD into CRD cache
          const resourceDef = KubernetesOperatorClient.apiResourceVersionMap[cluster || this.currentCluster.name]
          if (resourceDef && resObj.kind === K8SResourceType.CustomResourceDefinition) {
            const obj = resObj as V1CustomResourceDefinition
            resourceDef.kgv.push({
              apiGroup: obj.spec.group,
              kind: obj.spec.names.kind,
              name: obj.spec.names.plural,
              namespacedRes: obj.spec.scope === "Namespaced",
              version: obj.spec.versions[obj.spec.versions.length - 1]?.name
            })
            this.log.debug(`add custom resource definition into CRD cache`)
          }
          if (this.log.isLevelEnabled("trace")) {
            this.log.trace(`raw results: \n${yaml.dump(body)}`)
          }
          return
        }
        if (e.statusCode === 409) {
          this.log.info(`${resObj.kind} - ${resObj.apiVersion} - ${resName} already exists, skipped.`)
          return
        }
        this.log.error(e, `apply kubernetes resource '${resObj.kind}/${resName}' of apiVersion '${resObj.apiVersion}' failed: ${e.statusCode}`)
      }
      throw e
    }
  }

  async delete(resource: K8SResourceMeta, cluster?: string): Promise<void> {
    const namespace = resource.namespace || this.appMetadata.namespace
    this.log.info(`deleting ${resource.kind}: ${resource.name} in namespace '${namespace}'`)
    if (this.dryRunMode) {
      return
    }
    try {
      const { body, response } = await this.getApiClient(cluster).k8sApi.delete({
        kind: resource.kind,
        metadata: {
          name: resource.name,
          namespace
        },
        apiVersion: resource.apiVersion || this.getApiVersionsByKind(resource.kind, undefined, cluster)
      })
      if (!this.deletedK8SResources.has(resource.kind)) {
        this.deletedK8SResources.set(resource.kind, [])
      }
      this.deletedK8SResources.get(resource.kind)?.push({
        kind: resource.kind,
        metadata: {
          name: resource.name,
          namespace
        }
      })
      if (resource.kind === K8SResourceType.Namespace && resource.name) {
        // remove deleted namespace
        this.clusterNamespaces
          .get(cluster || this.currentCluster.name)
          ?.delete(resource.name)
      }
      this.log.info(`${resource.kind} '${resource.name}' deleted, status: ${response.statusCode} ${response.statusMessage} \n`)
      if (this.log.isLevelEnabled("trace")) {
        this.log.trace(`raw results: \n${yaml.dump(body)}`)
      }
    } catch (e) {
      if (e instanceof HttpError && e.statusCode === 404) {
        this.log.warn(`the resource to delete isn't found: ${resource.kind} - ${resource.name}`)
        return
      }
      throw e
    }

  }

  async namespaceExists(ns: string, cluster?: string): Promise<boolean> {
    cluster = cluster ?? this.currentCluster.name
    const nsSet = this.clusterNamespaces.get(cluster)
    if (!nsSet) {
      await this.pullNamespaces(cluster)
    }
    return this.clusterNamespaces.get(cluster)!.has(ns)
  }

  async getPods(param: PodQueryParam, cluster?: string): Promise<V1Pod[]> {
    let labels = {} as KVString
    if (param.labelSelectors) {
      labels = Object.assign({}, param.labelSelectors)
    }
    if (param.appLabelName) {
      labels.app = param.appLabelName
    }
    return await this.find({
      name: param.podName,
      namespace: param.namespace || this.appMetadata.namespace,
      kind: K8SResourceType.Pod
    }, labels, cluster)
  }

  async getConf<Query extends ConfQueryParam | SecretQueryParam>(param: Query, cluster?: string): Promise<GetConfReturn<Query> | undefined> {
    const ns = param.namespace || this.appMetadata.namespace
    const client = this.getApiClient(cluster).coreV1Api
    const isSecret = 'secret' in param && param['secret']
    const type = isSecret ? 'Secret' : 'ConfigMap'
    this.log.info(`start fetching ${type} '${param.name}' of namespace ${ns}`)
    if (this.dryRunMode) {
      return
    }
    try {
      if (isSecret) {
        const { body, response } = await client.readNamespacedSecret(param.name, ns)
        this.log.info(`secret ${param.name} found, res: ${response.statusCode} ${response.statusMessage}`)
        if (body.data) {
          const decode = {} as KVString
          for (const key in body.data) {
            decode[key] = Buffer.from(body.data[key], 'base64').toString()
          }
          return {
            name: param.name,
            data: decode,
            namespace: body.metadata?.namespace,
            metadata: body.metadata,
            secret: true
          } as GetConfReturn<Query>
        }
        return
      } else {
        const { body, response } = await client.readNamespacedConfigMap(param.name, ns)
        this.log.info(`configMap ${param.name} found, res: ${response.statusCode} ${response.statusMessage}`)
        if (this.log.isLevelEnabled("trace")) {
          this.log.trace(`raw results: \n${yaml.dump(body)}`)
        }
        return {
          name: param.name,
          data: body.data ?? {},
          namespace: body.metadata?.namespace,
          metadata: body.metadata,
          secret: false,
        } as GetConfReturn<Query>
      }
    } catch (err) {
      if (err instanceof HttpError) {
        // not found, then use create method instead
        if (err.statusCode == 404) {
          this.log.info(`${type} - ${param.name} is not found`)
          return
        }
      }
      throw err
    }
  }

  async saveConf(conf: ConfigMap | Secret, cluster?: string): Promise<void> {
    if (!conf.data) {
      this.log.info(`no content in config/secret '${conf.name}', operation skipped`)
      return
    }
    const resType = conf.secret ? K8SResourceType.Secret : K8SResourceType.ConfigMap
    const obj = this.makeResource(conf.name, resType, undefined, undefined, conf.namespace, conf.metadata?.annotations, conf.metadata?.labels)
    const tmp = (obj as KV)
    if (conf.secret) {
      tmp.data = {}
      for (const key in conf.data) {
        (tmp.data as KVString)[key] = Buffer.from(conf.data[key]).toString('base64')
      }
      tmp.type = conf.type
    } else {
      tmp.data = conf.data
    }
    await this.apply(obj, true, cluster)
  }

  async runJob(param: JobParam, cluster?: string): Promise<void> {
    let obj: V1JobSpec = param.jobSpec || {} as V1JobSpec
    // simple spec mode
    if (param.simpleSpec) {
      const { volumes, volumeMounts } = this.generateVolumeSpec(param.simpleSpec)
      obj = {
        ttlSecondsAfterFinished: 300,
        backoffLimit: 3,
        parallelism: 1,
        completions: 1,
        template: {
          metadata: {
            labels: {
              job: param.name
            }
          },
          spec: {
            restartPolicy: 'OnFailure',
            affinity: param.simpleSpec.affinity,
            hostNetwork: param.simpleSpec.hostNetwork,
            hostPID: param.simpleSpec.hostPid,
            hostIPC: param.simpleSpec.hostIPC,
            securityContext: param.simpleSpec.securityContext,
            serviceAccount: param.simpleSpec.serviceAccountName,
            serviceAccountName: param.simpleSpec.serviceAccountName,
            volumes,
            containers: [{
              image: param.simpleSpec.image,
              name: param.name,
              command: param.simpleSpec.command,
              args: param.simpleSpec.arguments,
              resources: param.simpleSpec.resources,
              env: param.simpleSpec.env,
              volumeMounts
            }]
          }
        }
      }
    } else {
      // complete spec mode
      !obj.ttlSecondsAfterFinished && (obj.ttlSecondsAfterFinished = 300)
      !obj.parallelism && (obj.parallelism = 1)
      !obj.completions && (obj.completions = 1)
    }

    const res = this.makeResource(param.name, K8SResourceType.Job, obj, undefined, param.namespace)
    await this.apply(res, true, cluster)

    const pods = await this.getPods({
      namespace: param.namespace,
      labelSelectors: {
        job: param.name
      }
    }, cluster)
    if (param.followJobLog) {
      this.fetchLogs({
        namespace: param.namespace,
        pod: pods[0].metadata?.name || "",
        tail: true,
        prependTimestamp: true,
        followAndPipeTo: param.followJobLog,
        onComplete: (err) => {
          if (err) {
            this.log.error(err, `can not follow logs of job: ${param.name}`)
          } else {
            this.log.info(`job ${param.name} finish, stop tailing logs`)
          }
        }
      })
    }
    // wait until job finished
    await this.checkWorkloadStatus(K8SResourceType.Job, param.name, MAX_JOB_STATUS_CHECK_TIME)
  }

  async checkWorkloadStatus(
    type: CheckableWorkloadType, name: string, maxCheckTimeMills?: number,
    namespace?: string, cluster?: string, foundSpec?: KubernetesObject
  ): Promise<void> {
    this.log.info(`start to check ${type} status of ${name}`)
    if (this.dryRunMode) {
      return
    }
    let checkTimes = (maxCheckTimeMills || DEFAULT_STATUS_CHECK_TIME) / STATUS_CHECK_INTERVAL
    if (checkTimes < 1) {
      checkTimes = 1
    }
    for (let i = 0; i < checkTimes; i++) {
      let workload = foundSpec
      let status
      let spec
      if (!foundSpec) {
        this.disableLog()
        const workloads: KubernetesObject[] = await this.find({ kind: type, namespace: namespace }, { workload: name }, undefined, cluster)
        // recover log level after query end
        this.enableLog()
        if (workloads.length === 0 || !((workloads[0] as KV).status)) {
          throw new Error(`workload ${name} of type ${type} not found`)
        }
        workload = workloads[0]
      }
      switch (type) {
        case K8SResourceType.Pod:
          spec = (workload as V1Pod).spec!
          status = (workload as V1Pod).status!
          if (status.phase === "Failed") {
            throw new Error(`pod ${name} failed`)
          } else if (status.conditions) {
            let ready = true
            status.conditions.forEach(cond => {
              if (cond.status !== 'True') {
                ready = false
              }
            })
            status.containerStatuses?.forEach(containerStatus => {
              if (!containerStatus.ready) {
                ready = false
              }
            })
            if (ready) {
              this.log.info(`pod ${name} is ready`)
              return
            }
          }
          break
        case K8SResourceType.Deployment:
          spec = (workload as V1Deployment).spec!
          status = (workload as V1Deployment).status!
          this.log.info(`checked Deployment status of ${name}: ${status.readyReplicas || 0} pods ready, ${status.unavailableReplicas || 0} pods unavailable, ${status.updatedReplicas || 0} updated, ${status.replicas || 0} existing pods. ${spec.replicas || 0} expected`)
          if (spec.replicas === 0 || (status.replicas === spec.replicas && status.readyReplicas === spec.replicas &&
            (status.unavailableReplicas || 0) === 0 && status.updatedReplicas === spec.replicas)) {
            this.log.info(`Deployment ${name} is running well, ready replicas: ${spec.replicas || 0}`)
            return
          }
          break
        case K8SResourceType.StatefulSet:
          spec = (workload as V1StatefulSet).spec!
          status = (workload as V1StatefulSet).status!
          this.log.info(`checked StatefulSet status of ${name}: ${status.readyReplicas || 0} pods ready, ${status.updatedReplicas || 0} updated, ${status.replicas || 0} existing pods. ${spec.replicas || 0} expected`)
          if (spec.replicas === 0 || (status.replicas === spec.replicas && status.readyReplicas === spec.replicas && status.updatedReplicas === spec.replicas)) {
            this.log.info(`StatefulSet ${name} is running well, ready replicas: ${spec.replicas || 0}`)
            return
          }
          break
        case K8SResourceType.DaemonSet:
          spec = (workload as V1DaemonSet).spec!
          status = (workload as V1DaemonSet).status!
          this.log.info(`checked Daemonset status of ${name}: ${status.numberAvailable || 0} pods available, ${status.numberUnavailable || 0} unavailable, ${status.numberReady || 0} pods ready. ${status.desiredNumberScheduled || 0} expected`)
          if (status.numberAvailable === status.desiredNumberScheduled && status.numberReady === status.desiredNumberScheduled) {
            this.log.info(`Daemonset ${name} is running well, ready number: ${status.numberReady || 0}`)
            return
          }
          break
        case K8SResourceType.Job:
          spec = (workload as V1Job).spec!
          status = (workload as V1Job).status!
          this.log.info(`checked job status of ${name}: ${status.active || 0} active, ${status.failed || 0} failed, ${status.succeeded || 0} succeeded.`)
          if ((status.succeeded || 0) >= (spec.completions || 1)) {
            this.log.info(`Job ${name} succeeded, completions: ${spec.completions || 0}`)
            return
          }
          if ((status.failed || 0) >= (spec.completions || 1)) {
            this.log.info(`Job ${name} failed, failures: ${status.failed || 0}`)
            throw new Error(`job ${name} failed`)
          }
          break
        default:
          throw new Error(`check status of type ${type} isn't supported`)
      }
      if (i !== checkTimes - 1) {
        await wait(STATUS_CHECK_INTERVAL)
      }

    }
    throw new Error(`${type} - ${name} still not ready`)
  }

  async portForward(param: ForwardParam, cluster?: string): Promise<ForwardOrExecResult> {
    if (this.dryRunMode) {
      throw new Error(`can not forward port in dry-run mode`)
    }
    const apiClient = this.getApiClient(cluster)
    let podName = param.podName
    if (!podName) {
      const podList = await this.getPods({ labelSelectors: param.labelSelectors, namespace: param.namespace, podName: param.podName }, cluster)
      const runningPods = podList.filter(p => p.status?.phase === "Running")
      if (runningPods.length === 0) {
        throw new Error('can not find any pod to forward')
      }
      // choose random one in case of pod not ready
      podName = _.sample(runningPods)?.metadata?.name
    }
    const forward = new PortForward(apiClient.kubeConf)
    const svr = net.createServer(async (socket) => {
      try {
        await forward.portForward(param.namespace || this.appMetadata.namespace, podName as string,
          [param.containerPort], socket, null, socket, param.maxCheckTimes || MAX_FORWARD_CHECK_TIMES)
      } catch (e) {
        this.log.error(e as Error, "port forward failed")
      }
    })
    const host = param.bindAll ? '127.0.0.1' : '0.0.0.0'
    let connCnt = 0
    svr.on("connection", (socket) => {
      this.log.info(`handle connection to pod ${podName} from ${socket.remoteAddress + ":" + socket.remotePort}, connections: ${++connCnt}`)
    }).on("error", (err) => {
      this.log.error(err, "port forward failed because of server error")
    }).on("listening", () => {
      this.log.info(`forwarding ${host}:${(svr.address() as net.AddressInfo).port} to port ${param.containerPort} of pod: ${podName} started`)
    }).listen(param.targetPort || 0, host)
    return {
      targetPod: podName as string, stop: async () => {
        await promisify(svr.close).call(svr)
      }
    }
  }

  async exec(param: ExecParam, cluster?: string): Promise<ForwardOrExecResult> {
    const cmd = param.command || EXEC_DEFAULT_CMD
    this.log.info(`exec command ${cmd} on pod '${param.podName || JSON.stringify(param.labelSelectors)}', in container ${param.container}`)
    if (this.dryRunMode) {
      return {
        targetPod: "dry-run",
        stop: async () => {
          console.log('dry-run stop')
        }
      }
    }
    const apiClient = this.getApiClient(cluster)
    let podName = param.podName
    if (!podName) {
      const podList = await this.getPods({ labelSelectors: param.labelSelectors, namespace: param.namespace, podName: param.podName }, cluster)
      if (podList.length === 0) {
        throw new Error('can not find any pod to forward')
      }
      podName = podList[0].metadata?.name
    }
    const exec = new Exec(apiClient.kubeConf)
    const ws = await exec.exec(param.namespace || this.appMetadata.namespace, podName as string, param.container, cmd,
      param.stdout || process.stdout, param.stderr || process.stderr, param.stdin || process.stdin, true, (status) => {
        this.log.debug(`receive exec status event:\n${yaml.dump(status)}`)
      })
    ws.on('error', (err) => {
      this.log.error(err, 'exec websocket error')
    }).on('open', () => {
      this.log.info(`exec commands in pod ${podName}`)
    }).on('close', (code, reason) => {
      param.onExit && param.onExit(code, reason.toString())
      this.log.info(`exec channel of ${podName} closed, code: ${code}, reason: ${reason || 'no reason'}`)
    })
    return {
      targetPod: podName || "",
      stop: async () => {
        ws.close()
      }
    }
  }

  async fetchLogs(param: FetchLogParam, cluster?: string): Promise<string[] | undefined> {
    this.log.debug(`fetch logs of pod: ${param.pod}, param: ${JSON.stringify(_.omit(param, "followAndPipeTo", "onFollowError"))}`)
    if (this.dryRunMode) {
      return
    }
    const limit = param.limit || MAX_LINES_OF_LOGS
    const result = []
    const containers = param.containers || [undefined]
    const ns = param.namespace || this.appMetadata.namespace
    for (const container of containers) {
      if (!param.followAndPipeTo) {
        const { body, response } = await this.getApiClient(cluster).coreV1Api.readNamespacedPodLog(
          param.pod, ns,
          container, false, true, limit * MAX_BYTES_PER_LINE, undefined,
          param.previous, param.sinceSeconds, limit, param.prependTimestamp
        )
        result.push(body)
        this.log.debug(`pod logs fetched, res: ${response.statusCode} ${response.statusMessage}, total bytes: ${body.length}`)
      } else {
        // follow mode, use raw request instead
        this.log.debug(`pod logs followed, will pipe into writable stream`)
        this.pipeRequest(`api/v1/namespaces/${ns}/pods/${param.pod}/log`, 'GET', param.followTimeout || 3600000, undefined,
          cluster, param.followAndPipeTo, param.onComplete, {
          qs: { follow: true, previous: param.previous, tailLines: limit, sinceSeconds: param.sinceSeconds, timestamps: param.prependTimestamp, container: container }
        })
      }
    }
    return param.followAndPipeTo ? undefined : result
  }

  getBuiltInLabels(): KVString {
    return this.builtInLabels
  }

  disableLog(): void {
    tmpLevel = this.log.level === LEVEL_DISABLE ? tmpLevel : this.log.level
    this.log.level = LEVEL_DISABLE
  }

  enableLog(): void {
    this.log.level = tmpLevel
  }

  crdExists(kind: string, apiGroup?: string | undefined, cluster?: string): boolean {
    if (this.dryRunMode) {
      return true
    }
    return !!this.getResourceDefinition(kind, apiGroup, cluster)
  }

  /**
   * Make customize API client to implement anything that couldn't be done by other pre-defined methods.
   */
  makeApiClient<T extends ApiType>(apiClientType: ApiConstructor<T>, cluster?: string): T {
    return this.getApiClient(cluster).kubeConf.makeApiClient(apiClientType)
  }

  /**
   * Factory method to create KubernetesResource object that kind/apiVersion/metadata are set by YAM engine
   */
  makeResource<T extends KubernetesObject>(
    name: string, kind: K8SResourceType | string, spec: unknown, apiVersion?: string,
    namespace?: string, annotations?: KVString, extraLabels?: KVString,
  ): T {
    this.log.info(`generate resource object: ${kind} - ${name}`)
    const res = {
      kind,
      apiVersion: apiVersion || this.getApiVersionsByKind(kind),
      metadata: {
        name,
        namespace: namespace || this.appMetadata.namespace,
        labels: extraLabels,
        annotations
      },
      spec
    } as KubernetesObject
    return res as unknown as T
  }

  async rawRequest(path: string, method: string, reqBody?: unknown, cluster?: string, extraOpts?: request.CoreOptions):
    Promise<{ body: unknown, response: request.Response }> {
    const start = Date.now()
    const reqId = crypto.randomBytes(8).toString("hex")
    this.log.debug(`sending request ${reqId} to API server: ${method.toUpperCase()} - ${path}`)

    if (this.dryRunMode) {
      this.log.warn(`response statusCode 202 for raw request '${reqId}' in 'DRY-RUN' mode, please handle it properly`)
      return DRY_RUN_RESULT
    }

    const opts = this.applyRequestOpts(method, reqBody, cluster, extraOpts)
    return new Promise((resolve, reject) => {
      request(path, opts, (err, response, body) => {
        this.log.debug(`request ${reqId} finished, status: ${response?.statusCode}, cost ${Date.now() - start}ms`)
        if (err) {
          reject(err)
        } else {
          resolve({ body, response })
        }
      })
    })
  }


  /**
   * customized apiVersion mapping set by ~/.yam/config.yaml
   */
  private static mergeCustomizedApiVersionMapping(
    kClient: KubernetesOperatorClient, cluster: KubernetesEnvironment, customizedMappings?: EnvPatternValues<KVString>[]
  ): void {
    if (!customizedMappings) {
      return
    }
    const customized = reduceWildcardEnvValue(cluster.name, customizedMappings)
    for (const kind in customized) {
      const newGV = customized[kind].split('/')
      let matched = false
      this.apiResourceVersionMap[cluster.name].kgv.forEach(kgv => {
        if (kgv.kind === kind) {
          matched = true
          kgv.apiGroup = newGV[0] || ''
          kgv.version = newGV[1] || ''
          if (kClient.log.isLevelEnabled('debug')) {
            kClient.log.warn(`the apiVersion of '${kgv.kind}' will be overridden to ${customized[kind]}`)
          }
        }
      })
      if (!matched) {
        this.apiResourceVersionMap[cluster.name].kgv.push({
          kind,
          name: newGV[0] + '-yam-engine-customized',
          apiGroup: newGV[0] || '',
          version: newGV[1] || '',
          namespacedRes: true
        })
      }

    }
  }

  private pipeRequest(
    path: string, method: string, timeout: number, reqBody?: unknown, cluster?: string,
    target?: NodeJS.WritableStream, onError?: (e?: Error) => void, extraOpts?: request.CoreOptions,
  ): void {
    if (!target) {
      throw new Error('no pipe target')
    }
    this.log.debug(`pipe request to API server: ${method.toUpperCase()} - ${path}`)
    if (this.dryRunMode) {
      return
    }
    const opts = this.applyRequestOpts(method, reqBody, cluster, extraOpts)
    const defaultErrHandler = (err: Error) => err && this.log.error(err)
    // API server will disconnect/complete the request in 2 minutes, configure timeout to make sure client side won't disconnect
    opts.timeout = timeout
    request(path, opts).on('error', onError || defaultErrHandler).on("complete", () => {
      onError && onError()
    }).pipe(target)
  }

  private applyRequestOpts(method: string, reqBody?: unknown, cluster?: string, extraOpts?: request.CoreOptions) {
    const kc = this.getApiClient(cluster).kubeConf
    const svr = kc.getCurrentCluster()?.server
    let opts = { timeout: K8S_REQUEST_TIMEOUT, method, body: reqBody, baseUrl: svr, strictSSL: false } as request.Options
    if (extraOpts) {
      opts = Object.assign(opts, extraOpts)
    }
    kc.applyToRequest(opts)
    return opts
  }

  private getApiClient(cluster?: string): KubeApiClient {
    const client = this.clusterConfigs.get(cluster || this.currentCluster.name)
    if (!client) {
      throw new Error(cluster + " not found in kube-context")
    }
    return client
  }

  getCurrentCluster(): KubernetesEnvironment {
    return this.currentCluster
  }

  private getApiVersionsByKind(kind: string, group?: string, cluster?: string): string | undefined {
    const matchedDef = this.getResourceDefinition(kind, group, cluster)
    if (matchedDef) {
      if (matchedDef.version) {
        const res = matchedDef.apiGroup + '/' + matchedDef.version
        this.log.debug(`find api group of ${kind}, apiVersion: ${res}`)
        return res
      } else {
        // v1 API like Node/Pod/ConfigMap/Namespace, not in specified group
        return matchedDef.apiGroup
      }
    }
    return WELL_KNOWN_API_VERSION[kind] || 'v1'
  }

  private getResourceDefinition(kind: string, group?: string, cluster?: string): KindGroupVersion | undefined {
    const resourceDef = KubernetesOperatorClient.apiResourceVersionMap[cluster || this.currentCluster.name]
    if (!resourceDef) {
      return
    }
    const matchedDef = resourceDef.kgv.find(def => {
      if (group && def.apiGroup !== group) {
        return false
      }
      return def.kind.toLowerCase() === kind.toLowerCase()
    })
    return matchedDef
  }

  private generateVolumeSpec(simpleSpec: SimplePodSpec): { volumes: V1Volume[], volumeMounts: V1VolumeMount[] } {
    const volumes: V1Volume[] = []
    const volumeMounts: V1VolumeMount[] = []
    simpleSpec?.configMapMount?.forEach(cfg => {
      volumes.push({
        name: cfg.name,
        configMap: {
          name: cfg.name
        }
      })
      volumeMounts.push({
        name: cfg.name,
        mountPath: cfg.mountPath,
      })
    })
    simpleSpec?.secretMount?.forEach(secret => {
      volumes.push({
        name: secret.name,
        secret: {
          secretName: secret.name
        }
      })
      volumeMounts.push({
        name: secret.name,
        mountPath: secret.mountPath,
      })
    })
    return {
      volumes,
      volumeMounts
    }
  }

  private async pullNamespaces(cluster: string) {
    if (this.dryRunMode) {
      this.clusterNamespaces.set(cluster, new Set())
      return
    }
    const { body } = await this.getApiClient(cluster).coreV1Api.listNamespace()
    this.clusterNamespaces.set(cluster, new Set(body.items.map(i => i.metadata?.name).filter(ns => !!ns) as string[]))
  }
}


// Commonly used apiVersions if in dry-run mode (Fallback to v1 if not pre-defined)
const WELL_KNOWN_API_VERSION: KVString = {
  APIService: 'apiregistration.k8s.io/v1',
  TokenReview: 'authentication.k8s.io/v1',
  LocalSubjectAccessReview: 'authorization.k8s.io/v1',
  SelfSubjectAccessReview: 'authorization.k8s.io/v1',
  SelfSubjectRulesReview: 'authorization.k8s.io/v1',
  SubjectAccessReview: 'authorization.k8s.io/v1',
  CSIDriver: 'storage.k8s.io/v1',
  CSINode: 'storage.k8s.io/v1',
  StorageClass: 'storage.k8s.io/v1',
  VolumeAttachment: 'storage.k8s.io/v1',
  Event: 'events.k8s.io/v1',
  PodDisruptionBudget: 'policy/v1beta1',
  PodSecurityPolicy: 'policy/v1beta1',
  IngressClass: 'networking.k8s.io/v1',
  Ingress: 'networking.k8s.io/v1',
  NetworkPolicy: 'networking.k8s.io/v1',
  CustomResourceDefinition: 'apiextensions.k8s.io/v1',
  EndpointSlice: 'discovery.k8s.io/v1beta1',
  ControllerRevision: 'apps/v1',
  DaemonSet: 'apps/v1',
  Deployment: 'apps/v1',
  ReplicaSet: 'apps/v1',
  StatefulSet: 'apps/v1',
  Job: 'batch/v1',
  ClusterRoleBinding: 'rbac.authorization.k8s.io/v1',
  ClusterRole: 'rbac.authorization.k8s.io/v1',
  RoleBinding: 'rbac.authorization.k8s.io/v1',
  Role: 'rbac.authorization.k8s.io/v1',
  MutatingWebhookConfiguration: 'admissionregistration.k8s.io/v1',
  ValidatingWebhookConfiguration: 'admissionregistration.k8s.io/v1',
  CertificateSigningRequest: 'certificates.k8s.io/v1',
  PriorityClass: 'scheduling.k8s.io/v1',
  Lease: 'coordination.k8s.io/v1',
  HorizontalPodAutoscaler: 'autoscaling/v1',

  // Popular 3rd party CRDs: https://doc.crds.dev/
  ClusterIssuer: 'cert-manager.io/v1',
  Issuer: 'cert-manager.io/v1',
  Certificate: 'cert-manager.io/v1',
  CertificateRequest: 'cert-manager.io/v1',
  Challenge: 'acme.cert-manager.io/v1',
  Order: 'acme.cert-manager.io/v1',

  ApisixUpstream: 'apisix.apache.org/v2beta3',
  ApisixTls: 'apisix.apache.org/v2beta3',
  ApisixRoute: 'apisix.apache.org/v2beta3',
  ApisixConsumer: 'apisix.apache.org/v2beta3',
  ApisixPluginConfig: 'apisix.apache.org/v2beta3',
  ApisixClusterConfig: 'apisix.apache.org/v2beta3',

  TigeraStatus: 'operator.tigera.io/v1',
  ImageSet: 'operator.tigera.io/v1',
  APIServer: 'operator.tigera.io/v1',
  Installation: 'operator.tigera.io/v1',

  KongIngress: 'configuration.konghq.com/v1',
  KongClusterPlugin: 'configuration.konghq.com/v1',
  KongConsumer: 'configuration.konghq.com/v1',
  KongPlugin: 'configuration.konghq.com/v1',

  TraefikService: 'traefik.containo.us/v1alpha1',
  IngressRoute: 'traefik.containo.us/v1alpha1',
  IngressRouteTCP: 'traefik.containo.us/v1alpha1',
  IngressRouteUDP: 'traefik.containo.us/v1alpha1',

  Alertmanager: 'monitoring.coreos.com/v1',
  PodMonitor: 'monitoring.coreos.com/v1',
  ServiceMonitor: 'monitoring.coreos.com/v1',
  Prometheus: 'monitoring.coreos.com/v1',
  ThanosRuler: 'monitoring.coreos.com/v1',
  PrometheusRule: 'monitoring.coreos.com/v1',
  Probe: 'monitoring.coreos.com/v1',
}
